import type { InfoClient, SubscriptionClient } from "@nktkas/hyperliquid";
import type {
	Bar,
	DatafeedConfiguration,
	IBasicDataFeed,
	LibrarySymbolInfo,
	SearchSymbolResultItem,
} from "@public/charting_library";
import { notifyChartRealtimeStale } from "#/lib/api/notify-chart-stale.ts";
import { notifyIfRateLimit } from "#/lib/api/notify-rate-limit.ts";
import type { TradePerpMarketSummary } from "../hooks/use-trade-data.ts";
import {
	createTradeMarketIdentity,
	getApiCoin,
	getInfoClient,
	getSubscriptionClient,
	getSupportedChartResolutions,
	resolutionToInterval,
} from "./hyperliquid";

type CandleSnapshot = Awaited<ReturnType<InfoClient["candleSnapshot"]>>[number];
type CandleSubscription = Awaited<ReturnType<SubscriptionClient["candle"]>>;

const datafeedConfiguration: DatafeedConfiguration = {
	supported_resolutions: getSupportedChartResolutions(),
	supports_marks: false,
	supports_timescale_marks: false,
	supports_time: true,
};

function countPriceScale(price: string) {
	const decimals = price.split(".")[1]?.length ?? 0;
	return 10 ** Math.max(0, Math.min(decimals, 8));
}

function toBar(candle: CandleSnapshot): Bar {
	return {
		time: candle.t,
		open: Number(candle.o),
		high: Number(candle.h),
		low: Number(candle.l),
		close: Number(candle.c),
		volume: Number(candle.v),
	};
}

type SearchableMarket = Pick<
	TradePerpMarketSummary,
	"coin" | "dex" | "displayName" | "displaySymbol" | "routeSymbol"
>;

type CreateHyperliquidDatafeedOptions = {
	getSearchableMarkets?: () => SearchableMarket[];
	onLoadError?: (message: string) => void;
	onLoadSuccess?: () => void;
};

function createSymbolInfo(
	symbol: string,
	lastPrice = "1000",
): LibrarySymbolInfo {
	const market = createTradeMarketIdentity(symbol);
	const chartLabel = `${market.coin}USD`;
	return {
		name: chartLabel,
		ticker: market.chartSymbol,
		description: chartLabel,
		type: "crypto",
		session: "24x7",
		exchange: "Hyperliquid",
		listed_exchange: "Hyperliquid",
		timezone: "Etc/UTC",
		format: "price",
		minmov: 1,
		pricescale: countPriceScale(lastPrice),
		has_intraday: true,
		has_weekly_and_monthly: true,
		supported_resolutions: getSupportedChartResolutions(),
		volume_precision: 4,
		data_status: "streaming",
	};
}

function normalizeSearchValue(value: string) {
	return value.trim().toLowerCase();
}

function buildSearchResultItem(
	market: SearchableMarket,
): SearchSymbolResultItem {
	return {
		symbol: market.displaySymbol,
		ticker: market.routeSymbol,
		description: market.displayName,
		exchange: market.dex ? "Hyperliquid XYZ" : "Hyperliquid",
		type: "crypto",
	};
}

function searchMarkets(
	markets: SearchableMarket[],
	userInput: string,
	exchange?: string,
): SearchSymbolResultItem[] {
	const query = normalizeSearchValue(userInput);
	const normalizedExchange = normalizeSearchValue(exchange ?? "");

	return markets
		.filter((market) => {
			const marketExchange = market.dex ? "hyperliquid xyz" : "hyperliquid";
			if (
				normalizedExchange &&
				marketExchange !== normalizedExchange &&
				market.dex !== normalizedExchange
			) {
				return false;
			}

			if (!query) return true;

			return [
				market.coin,
				market.routeSymbol,
				market.displaySymbol,
				market.displayName,
			].some((value) => normalizeSearchValue(value).includes(query));
		})
		.map(buildSearchResultItem);
}

export function createHyperliquidDatafeed(
	options: CreateHyperliquidDatafeedOptions = {},
): IBasicDataFeed & {
	destroy: () => void;
} {
	const infoClient = getInfoClient();
	const subscriptions = new Map<string, CandleSubscription>();
	// Tracks in-flight subscribeBars invocations so unsubscribeBars can cancel
	// pending retries / late-resolving promises before they wire a stale
	// subscription against a listenerGuid TV has already moved past.
	const pendingSubscribes = new Map<string, { cancelled: boolean }>();
	const getSearchableMarkets = options.getSearchableMarkets ?? (() => []);
	const { onLoadError, onLoadSuccess } = options;

	return {
		onReady(callback) {
			window.setTimeout(() => callback(datafeedConfiguration), 0);
		},

		searchSymbols(userInput, exchange, _symbolType, onResult) {
			onResult(searchMarkets(getSearchableMarkets(), userInput, exchange));
		},

		async resolveSymbol(symbolName, onResolve, _onError) {
			try {
				const apiCoin = getApiCoin(symbolName);
				const now = Date.now();
				const candles = await infoClient.candleSnapshot({
					coin: apiCoin,
					interval: "1h",
					startTime: now - 3_600_000,
					endTime: now,
				});
				const lastPrice =
					candles.length > 0 ? candles[candles.length - 1].c : undefined;
				onResolve(createSymbolInfo(symbolName, lastPrice));
			} catch {
				onResolve(createSymbolInfo(symbolName));
			}
		},

		async getBars(symbolInfo, resolution, periodParams, onResult, onError) {
			const coin = getApiCoin(symbolInfo.ticker ?? symbolInfo.name);
			const interval = resolutionToInterval(resolution);
			const maxAttempts = periodParams.firstDataRequest ? 4 : 1;

			let lastError: unknown;
			for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
				try {
					const bars = await infoClient.candleSnapshot({
						coin,
						interval,
						startTime: periodParams.from * 1000,
						endTime: periodParams.to * 1000,
					});
					const mappedBars = bars.map(toBar);
					onResult(mappedBars, { noData: mappedBars.length === 0 });
					if (periodParams.firstDataRequest) onLoadSuccess?.();
					return;
				} catch (error) {
					lastError = error;
					notifyIfRateLimit(error);
					if (attempt < maxAttempts - 1) {
						await new Promise((resolve) =>
							setTimeout(resolve, 1000 * 2 ** attempt),
						);
					}
				}
			}

			const message =
				lastError instanceof Error ? lastError.message : "Unable to fetch bars";
			if (periodParams.firstDataRequest) onLoadError?.(message);
			onError(message);
		},

		subscribeBars(symbolInfo, resolution, onTick, listenerGuid) {
			const coin = getApiCoin(symbolInfo.ticker ?? symbolInfo.name);
			const interval = resolutionToInterval(resolution);
			const state = { cancelled: false };
			pendingSubscribes.set(listenerGuid, state);

			function trySubscribe(attempt: number) {
				if (state.cancelled) return;
				const client = getSubscriptionClient();
				if (!client) {
					if (attempt < 3) {
						setTimeout(() => trySubscribe(attempt + 1), 1000);
					} else {
						pendingSubscribes.delete(listenerGuid);
						notifyChartRealtimeStale();
					}
					return;
				}

				client
					.candle({ coin, interval }, (payload) => {
						if (state.cancelled) return;
						onTick(toBar(payload));
					})
					.then((subscription) => {
						if (state.cancelled) {
							void subscription.unsubscribe();
							return;
						}
						subscriptions.set(listenerGuid, subscription);
						pendingSubscribes.delete(listenerGuid);
					})
					.catch(() => {
						if (state.cancelled) return;
						if (attempt < 3) {
							setTimeout(() => trySubscribe(attempt + 1), 1000);
						} else {
							pendingSubscribes.delete(listenerGuid);
							notifyChartRealtimeStale();
						}
					});
			}

			trySubscribe(0);
		},

		unsubscribeBars(listenerGuid) {
			const state = pendingSubscribes.get(listenerGuid);
			if (state) state.cancelled = true;
			pendingSubscribes.delete(listenerGuid);
			const subscription = subscriptions.get(listenerGuid);
			subscriptions.delete(listenerGuid);
			if (subscription) void subscription.unsubscribe();
		},

		getServerTime(callback) {
			callback(Math.floor(Date.now() / 1000));
		},

		destroy() {
			for (const state of pendingSubscribes.values()) {
				state.cancelled = true;
			}
			pendingSubscribes.clear();
			for (const subscription of subscriptions.values()) {
				void subscription.unsubscribe();
			}
			subscriptions.clear();
		},
	};
}
